import type { DragEndEvent, DragOverEvent, DragStartEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t, Trans } from "@lingui/macro";
import { 
  ArrowCounterClockwise, 
  DotsSixVertical, 
  Plus, 
  TrashSimple,
  Eye,
  EyeSlash,
  Columns,
  Files,
  List
} from "@phosphor-icons/react";
import { defaultMetadata, defaultResumeData } from "@stylingcv/schema";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tooltip,
  Separator,
} from "@stylingcv/ui";
import type { LayoutLocator, SortablePayload } from "@stylingcv/utils";
import { cn, isEmptyString, moveItemInLayout, parseLayoutLocator } from "@stylingcv/utils";
import { useEffect, useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";

type ColumnProps = {
  id: string;
  name: string;
  items: string[];
};

const Column = ({ id, name, items }: ColumnProps) => {
  const { setNodeRef } = useDroppable({ id });

  return (
    <SortableContext id={id} items={items} strategy={verticalListSortingStrategy}>
      <div className="space-y-2">
        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest text-center">{name}</div>
        <div
          ref={setNodeRef}
          className="w-full bg-gray-50 border border-gray-300 rounded-lg border-dashed min-h-[100px] p-2 flex flex-col gap-2 relative"
        >
          {items.map((section) => (
            <SortableSection key={section} id={section} />
          ))}
        </div>
      </div>
    </SortableContext>
  );
};

type SortableSectionProps = {
  id: string;
};

const SortableSection = ({ id }: SortableSectionProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transition,
    opacity: isDragging ? 0.5 : 1,
    transform: CSS.Translate.toString(transform),
    position: "relative" as any,
    zIndex: isDragging ? 50 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <Section id={id} isDragging={isDragging} />
    </div>
  );
};

type SectionProps = {
  id: string;
  isDragging?: boolean;
};

const Section = ({ id, isDragging = false }: SectionProps) => {
  const { name, isHidden, isEmpty } = useResumeStore((state) => {
    try {
      let sectionName = "";
      let visible = true;
      let hasContent = true;

      if (id.startsWith("custom.")) {
        const customId = id.replace("custom.", "");
        const section = state.resume.data.sections.custom?.[customId];
        sectionName = section?.name || customId;
        visible = section?.visible ?? true;
        hasContent = (section?.items?.length ?? 0) > 0;
      } else {
        const section = (state.resume.data.sections as any)[id];
        sectionName = section?.name || id.charAt(0).toUpperCase() + id.slice(1);
        visible = section?.visible ?? true;

        hasContent =
          id === "summary"
            ? !!section?.content && section.content.trim() !== ""
            : (section?.items?.length ?? 0) > 0;
      }

      return { name: sectionName, isHidden: !visible, isEmpty: !hasContent };
    } catch {
      return { name: id.charAt(0).toUpperCase() + id.slice(1), isHidden: false, isEmpty: false };
    }
  });

  return (
    <div
      className={cn(
        "bg-white rounded-md p-2 flex items-center justify-between cursor-grab transition-colors shadow-sm group select-none relative overflow-hidden border border-gray-200",
        isDragging && "cursor-grabbing ring-2 ring-[#268599] ring-offset-1 ring-offset-white scale-105 z-50",
        isHidden ? "opacity-50" : "hover:border-[#268599]/40",
        !isHidden && !isEmpty && id !== "summary" && "border-gray-200",
        id === "skills" && !isHidden && "border-[#268599]/20 bg-[#268599]/5"
      )}
    >
      {/* Pattern for hidden items */}
      {isHidden && (
        <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPgo8cmVjdCB3aWR0aD0iOCIgaGVpZ2h0PSI4IiBmaWxsPSIjMDAwIiBmaWxsLW9wYWNpdHk9IjAiLz4KPHBhdGggZD0iTTAgOEw4IDBaTTAgMEw4IDhaIiBzdHJva2U9IiNmZmYiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz4KPC9zdmc+')" }}></div>
      )}
      
      <div className="flex items-center gap-2 overflow-hidden relative z-10">
        <DotsSixVertical
          size={16}
          weight="bold"
          className={cn(
            "text-gray-300 transition-colors",
            !isHidden && "group-hover:text-gray-500",
            id === "skills" && !isHidden && "group-hover:text-[#268599]"
          )}
        />
        <span className={cn(
          "text-xs font-medium truncate",
          isHidden ? "text-gray-400 line-through" : "text-[#16344c]",
          id === "skills" && !isHidden && "text-[#268599]"
        )}>
          {name}
        </span>
      </div>
      {id === "summary" && !isHidden && <List size={14} className="text-gray-300 relative z-10" />}
    </div>
  );
};

export const LayoutSection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const resumeData = useResumeStore((state) => state.resume.data);
  const layout = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.layout || defaultResumeData.metadata.layout;
    } catch {
      return defaultResumeData.metadata.layout;
    }
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [visibilityMode, setVisibilityMode] = useState<"show_all" | "hide_empty">("show_all");
  const [isProcessing, setIsProcessing] = useState(false);

  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id as string);
  };

  const onDragCancel = () => {
    setActiveId(null);
  };

  const onDragEvent = ({ active, over }: DragOverEvent | DragEndEvent) => {
    if (!over || !active.data.current) return;

    const currentPayload = active.data.current.sortable as SortablePayload | null;
    const current = parseLayoutLocator(currentPayload);

    if (active.id === over.id) return;

    try {
      if (!over.data.current) {
        const [page, column] = (over.id as string).split(".").map(Number);
        const target = { page, column, section: 0 } as LayoutLocator;

        const newLayout = moveItemInLayout(current, target, layout);
        setValue("metadata.layout", newLayout);
        return;
      }

      const targetPayload = over.data.current.sortable as SortablePayload | null;
      const target = parseLayoutLocator(targetPayload);

      const newLayout = moveItemInLayout(current, target, layout);
      setValue("metadata.layout", newLayout);
    } catch {
      // Silent fail
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    onDragEvent(event);
    setActiveId(null);
  };

  const onAddPage = () => {
    try {
      const layoutCopy = JSON.parse(JSON.stringify(layout));
      layoutCopy.push([[], []]);
      setValue("metadata.layout", layoutCopy);
    } catch {
      // Silent fail
    }
  };

  const onRemovePage = (page: number) => {
    try {
      const layoutCopy = JSON.parse(JSON.stringify(layout));

      layoutCopy[0][0].push(...layoutCopy[page][0]); // Main
      layoutCopy[0][1].push(...layoutCopy[page][1]); // Sidebar

      layoutCopy.splice(page, 1);

      setValue("metadata.layout", layoutCopy);
    } catch {
      // Silent fail
    }
  };

  const onResetLayout = () => {
    try {
      const layoutCopy = JSON.parse(JSON.stringify(defaultMetadata.layout));
      const customSections: string[] = [];

      for (const page of layout) {
        for (const column of page) {
          customSections.push(...column.filter((section) => section.startsWith("custom.")));
        }
      }

      let signatureIncluded = false;
      for (const page of layoutCopy) {
        for (const column of page) {
          if (column.includes("signature")) {
            signatureIncluded = true;
            break;
          }
        }
        if (signatureIncluded) break;
      }

      if (!signatureIncluded && layoutCopy.length > 0 && layoutCopy[0].length > 0) {
        layoutCopy[0][0].push("signature");
      }

      if (customSections.length > 0) layoutCopy[0][0].push(...customSections);

      setValue("metadata.layout", layoutCopy);
    } catch {
      // Silent fail
    }
  };

  const showAllSections = async () => {
    if (!resumeData?.sections) return;
    setIsProcessing(true);
    setVisibilityMode("show_all");

    try {
      const allSectionKeys = Object.keys(resumeData.sections);
      for (const sectionKey of allSectionKeys) {
        if (sectionKey === "custom") {
          const customSections = resumeData.sections.custom;
          if (customSections) {
            for (const customKey of Object.keys(customSections)) {
              setValue(`sections.custom.${customKey}.visible`, true);
            }
          }
        } else {
          setValue(`sections.${sectionKey}.visible`, true);
        }
      }

      toast({
        variant: "default",
        title: t`All Sections Visible`,
        description: t`All resume sections are now visible and ready for editing.`,
      });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Error`,
        description: t`Failed to show all sections. Please try again.`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const hideEmptySections = async () => {
    if (!resumeData?.sections) return;
    setIsProcessing(true);
    setVisibilityMode("hide_empty");

    try {
      const allSectionKeys = Object.keys(resumeData.sections);
      for (const sectionKey of allSectionKeys) {
        const section = (resumeData.sections as any)[sectionKey];

        if (sectionKey === "custom") {
          const customSections = resumeData.sections.custom;
          if (customSections) {
            for (const customKey of Object.keys(customSections)) {
              const customSection = customSections[customKey];
              if (!customSection?.items || customSection.items.length === 0) {
                setValue(`sections.custom.${customKey}.visible`, false);
              }
            }
          }
        } else if (sectionKey === "summary") {
          if (!section?.content || section.content.trim() === "" || section.content === "<p></p>") {
            setValue(`sections.${sectionKey}.visible`, false);
          }
        } else {
          if (!section?.items || section.items.length === 0) {
            setValue(`sections.${sectionKey}.visible`, false);
          }
        }
      }

      toast({
        variant: "default",
        title: t`Empty Sections Hidden`,
        description: t`Empty sections have been hidden to clean up your resume.`,
      });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Error`,
        description: t`Failed to hide empty sections. Please try again.`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    // onForceRefresh equivalent logic
    try {
      let layoutChanged = false;
      const layoutCopy = JSON.parse(JSON.stringify(layout));

      let signatureIncluded = false;
      for (const page of layout) {
        for (const column of page) {
          if (column.includes("signature")) {
            signatureIncluded = true;
            break;
          }
        }
        if (signatureIncluded) break;
      }

      if (!signatureIncluded && layout.length > 0 && layout[0].length > 0) {
        layoutCopy[0][0].push("signature");
        layoutChanged = true;
      }

      let hobbiesIncluded = false;
      for (const page of layout) {
        for (const column of page) {
          if (column.includes("hobbies")) {
            hobbiesIncluded = true;
            break;
          }
        }
        if (hobbiesIncluded) break;
      }

      if (!hobbiesIncluded && layout.length > 0 && layout[0].length > 1) {
        const sidebarColumn = layoutCopy[0][1];
        const interestsIndex = sidebarColumn.indexOf("interests");
        if (interestsIndex === -1) {
          sidebarColumn.push("hobbies");
        } else {
          sidebarColumn.splice(interestsIndex + 1, 0, "hobbies");
        }
        layoutChanged = true;
      }

      let mostProudOfIncluded = false;
      for (const page of layout) {
        for (const column of page) {
          if (column.includes("mostProudOf")) {
            mostProudOfIncluded = true;
            break;
          }
        }
        if (mostProudOfIncluded) break;
      }

      if (!mostProudOfIncluded && layout.length > 0 && layout[0].length > 1) {
        const mainColumn = layoutCopy[0][0];
        mainColumn.push("mostProudOf");
        layoutChanged = true;
      }

      let myMindIncluded = false;
      for (const page of layout) {
        for (const column of page) {
          if (column.includes("myMind")) {
            myMindIncluded = true;
            break;
          }
        }
        if (myMindIncluded) break;
      }

      if (!myMindIncluded && layout.length > 0 && layout[0].length > 1) {
        const mainColumn = layoutCopy[0][0];
        mainColumn.push("myMind");
        layoutChanged = true;
      }

      if (layoutChanged) {
        setValue("metadata.layout", layoutCopy);
      }
    } catch {
      // Silent fail
    }
  }, []);

  return (
    <div className="space-y-4">

      {/* Section Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-5">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
            <Columns size={20} className="text-gray-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#16344c]">{t`Layout`}</h3>
            <p className="text-[11px] text-gray-400">{t`Sections · pages · visibility`}</p>
          </div>
        </div>
        <Tooltip content={t`Reset Layout`}>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors" onClick={onResetLayout}>
            <ArrowCounterClockwise size={18} />
          </Button>
        </Tooltip>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">
      {/* Visibility Manager */}
      <div className="space-y-3 relative">
        <div className="flex items-center gap-2 text-gray-500">
          <Eye size={18} weight="duotone" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`Visibility Modes`}</span>
        </div>

        <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1.5 rounded-xl border border-gray-200">
          <button
            disabled={isProcessing}
            onClick={showAllSections}
            className={cn(
              "px-3 py-2.5 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2",
              visibilityMode === "show_all"
                ? "bg-white text-[#16344c] shadow-sm border border-gray-200"
                : "text-gray-400 hover:text-gray-600"
            )}>
            <Eye size={16} /> {t`Show All`}
          </button>
          <button
            disabled={isProcessing}
            onClick={hideEmptySections}
            className={cn(
              "px-3 py-2.5 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2",
              visibilityMode === "hide_empty"
                ? "bg-[#268599]/10 text-[#268599] shadow-sm border border-[#268599]/30"
                : "text-gray-400 hover:text-gray-600"
            )}>
            <EyeSlash size={16} className={visibilityMode === "hide_empty" ? "text-[#268599]" : ""} /> {t`Hide Empty`}
          </button>
        </div>
        <p className="text-[10px] text-gray-400 px-1 text-center">
          {visibilityMode === "hide_empty"
            ? t`Empty sections are safely hidden from the canvas.`
            : t`All sections are visible on the canvas.`}
        </p>
      </div>

      <Separator className="bg-gray-200" />

      {/* Pages Structure */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500">
            <Files size={18} weight="duotone" />
            <span className="text-xs font-semibold uppercase tracking-wider">{t`Document Structure`}</span>
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onDragCancel={onDragCancel}
        >
          {layout.map((page, pageIndex) => {
            const mainIndex = `${pageIndex}.0`;
            const sidebarIndex = `${pageIndex}.1`;

            const main = page[0] || [];
            const sidebar = page[1] || [];
            const pageNumber = pageIndex + 1;

            return (
              <div key={pageIndex} className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between bg-gray-50 px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-bold text-[#16344c]">{t`Page ${pageNumber}`}</span>
                  {pageIndex !== 0 && (
                    <Tooltip content={t`Remove Page`}>
                      <button
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        onClick={() => onRemovePage(pageIndex)}
                      >
                        <TrashSimple size={14} />
                      </button>
                    </Tooltip>
                  )}
                </div>

                <div className="p-3 grid grid-cols-2 gap-3 bg-gray-50">
                  <Column id={mainIndex} name={t`Main Flow`} items={main} />
                  <Column id={sidebarIndex} name={t`Sidebar`} items={sidebar} />
                </div>
              </div>
            );
          })}

          <Portal>
            <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
              {activeId && <Section isDragging id={activeId} />}
            </DragOverlay>
          </Portal>
        </DndContext>

        <button
          onClick={onAddPage}
          className="w-full mt-2 flex items-center justify-center gap-2 bg-[#268599]/5 border border-[#268599]/30 hover:border-[#268599]/60 text-[#268599] hover:bg-[#268599]/10 py-3 rounded-xl transition-all font-semibold text-xs uppercase tracking-wide"
        >
          <Plus size={18} weight="bold" />
          {t`Add New Page`}
        </button>
      </div>

      </div>
    </div>
  );
};
